import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Layout, Row, Col, Card, Button, Divider, message, Popover } from 'antd';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import FileUploader from '../file_uploader/FileUploader';
import {
	updateBidderStatus,
	requestsForProposalRestCrudThunksForSupplier,
} from '../../thunks/requests_for_proposal_thunks';
import { PageLoadingPlaceholder } from '../page_loading_placeholder/PageLoadingPlaceholder';
import WorkOrderRowDisplay from '../work_order_row_display/WorkOrderRowDisplay';
import { getRecordsForTargetCollection } from '../../reducers/standard_reducer_utils';
import { proposalsRestCrudThunksForSupplier } from '../../thunks/proposals_thunks';
import { Link } from 'react-router-dom';
import { currencyFormatter } from '../../utils/DataFormatterUtils';
import { EmptyState } from '../empty_state/EmptyState';
import LogOnMountWithStandardEventProperties from '../log_on_mount_with_standard_event_properties/LogOnMountWithStandardEventProperties';
import AwardStatusDisplay from '../rfp_row_display/AwardStatusDisplay';
import {
	compareStrings,
	getEntityById,
	nullSafeGet,
	nullSafeGetOrElse,
} from '../../utils/DataAccessUtils';
import { workOrdersRestCrudThunksForSupplier } from '../../thunks/work_orders_thunks';
import { RFP_STATUS } from '../../constants/BidderStatuses';

const { Content } = Layout;
require('./SupplierRFPDetailDetailsPage.less');
const PROPOSAL_TC_NAME = 'RFP_DETAIL_PROPOSALS';

const SupplierRFPDetailDetailsPage: FC<any> = ({
	getRequestForProposal,
	getProposals,
	match,
	requestForProposal,
	history,
	updateStatus,
	requestsForProposalFetching,
	proposalsFetching,
	proposals,
	currentUser,
	createWorkOrder,
}): React.ReactElement => {
	const [buttonLoading, setButtonLoading] = useState(false);
	const [workOrderCreating, setWorkOrderCreating] = useState(false);

	const requestForProposalId = parseInt(match.params.id, 10);

	const refresh = useCallback(() => {
		getRequestForProposal(requestForProposalId);
	}, []);

	useEffect(() => {
		getProposals({ requestForProposalId }, PROPOSAL_TC_NAME);
	}, []);

	const onCreateWorkOrder = useCallback(() => {
		const associatedProposals = getRecordsForTargetCollection(proposals, PROPOSAL_TC_NAME);
		const associatedWorkOrder = nullSafeGet('0.workOrder', associatedProposals);

		if (!associatedWorkOrder) {
			message.error('Unable to find associated work order! Please contact support!');
		} else {
			setWorkOrderCreating(true);
			const {
				problemTypeId,
				woPriorityId,
				spendCategoryId,
				locationId,
				assetId,
				images,
				description,
				buyerCompanyId,
			} = associatedWorkOrder;
			const entity = {
				supplierFacilityId: nullSafeGet('facility.id', currentUser),
				title: `Incurred cost for "${associatedWorkOrder.title}"`,
				createdBy: currentUser.email,
				problemTypeId,
				woPriorityId,
				spendCategoryId,
				locationId,
				assetId,
				images,
				description,
				buyerCompanyId,
			};
			createWorkOrder(entity)
				.then((newWorkOrder) => {
					history.push(`/supplier/workOrders/detail/${newWorkOrder.id}/details`);
				})
				.catch(message.error)
				.finally(() => setWorkOrderCreating(false));
		}
	}, [createWorkOrder, currentUser, history, proposals]);

	const customerFileAttachmentsAvailable = useMemo(
		() => nullSafeGet('attachments.0', requestForProposal),
		[requestForProposal]
	);

	const updateBidderStatus = useCallback(
		(status) => () => {
			setButtonLoading(true);
			updateStatus(requestForProposal, status)
				.then(() => {
					refresh();
				})
				.catch(message.error)
				.finally(() => setButtonLoading(false));
		},
		[refresh, requestForProposal, updateStatus]
	);

	const fetching = requestsForProposalFetching || proposalsFetching;

	const associatedProposals = getRecordsForTargetCollection(proposals, PROPOSAL_TC_NAME);
	const { bidderStatus } = requestForProposal;

	const isCloseRFP = useMemo(
		() => compareStrings(nullSafeGet('status', requestForProposal), RFP_STATUS.CLOSED),
		[requestForProposal]
	);

	const canCreateProposal = useMemo(
		() => !nullSafeGet('0', associatedProposals) && !isCloseRFP,
		[associatedProposals, isCloseRFP]
	);

	return fetching || !requestForProposal.id ? (
		<PageLoadingPlaceholder />
	) : (
		<Content className="rfpDetailDetailsPage" style={{ padding: '0 0.5em' }}>
			<LogOnMountWithStandardEventProperties eventType="visited supplier rfp detail page" />
			<Row
				style={{
					margin: '16px 4px 0',
				}}
				gutter={16}
			>
				<Col span={customerFileAttachmentsAvailable ? 16 : 24}>
					<Row style={{ margin: '0.5em -8px' }} gutter={16}>
						<Col span={24}>
							<div
								onClick={() =>
									history.push(`/supplier/workOrders/detail/${requestForProposal.workOrder.id}`)
								}
							>
								<Card title="About the Problem">
									<WorkOrderRowDisplay workOrder={requestForProposal.workOrder} hideDueDate />
								</Card>
							</div>
						</Col>
					</Row>
					{canCreateProposal ? (
						<Row style={{ margin: '0.5em -8px' }} gutter={16}>
							<Col span={24}>
								<Card>
									<h5>
										{bidderStatus === 'interested'
											? 'You showed interest in bidding. Are you ready to create a proposal?'
											: bidderStatus === 'notBidding'
											? `You are not participating, would you like to bid this work?`
											: `Are you planning to bid on this work?`}
									</h5>
									<div className={'mt-2'}>
										{bidderStatus === 'interested' ? (
											<Link
												to={`/supplier/opportunities/detail/${requestForProposalId}/newProposal?workOrderId=${nullSafeGet(
													'workOrder.id',
													requestForProposal
												)}`}
											>
												<Button loading={buttonLoading} type="primary">
													Yes, Create Proposal
												</Button>
											</Link>
										) : (
											<Button
												loading={buttonLoading}
												type="primary"
												onClick={updateBidderStatus('interested')}
											>
												Yes, I plan to bid
											</Button>
										)}
										{bidderStatus === 'notBidding' ? (
											''
										) : (
											<Button
												loading={buttonLoading}
												className={'ml-4'}
												onClick={updateBidderStatus('not_bidding')}
											>
												No, I don't want this work
											</Button>
										)}
									</div>
								</Card>
							</Col>
						</Row>
					) : null}
					{requestForProposal.description && requestForProposal.description !== '<p><br></p>' ? (
						<Row style={{ margin: '0.5em -8px' }} gutter={16}>
							<Col span={24}>
								<Card title="About the RFP">
									<div
										style={{ fontSize: 16, marginBottom: 24 }}
										className="richTextWrapper"
										dangerouslySetInnerHTML={{ __html: requestForProposal.description }}
									/>
								</Card>
							</Col>
						</Row>
					) : null}
					{associatedProposals && associatedProposals.length > 0 ? (
						<div>
							{associatedProposals.map((proposal) => (
								<div key={proposal.id} className="rowSpacing">
									<Card>
										<div
											style={{
												marginBottom: 24,
												display: 'flex',
												justifyContent: 'space-between',
												alignItems: 'center',
											}}
										>
											<div className="flex flex-col">
												<h5 style={{ marginBottom: 0 }}>Your Proposal</h5>
												<div>
													<AwardStatusDisplay awardStatus={requestForProposal.bidderAwardStatus} />
												</div>
											</div>
											{requestForProposal.bidderAwardStatus === 'pending' ? (
												<Button
													onClick={() => {
														history.push(
															`/supplier/opportunities/editProposal/${requestForProposalId}?proposalId=${proposal.id}`
														);
													}}
													disabled={isCloseRFP}
												>
													Edit
												</Button>
											) : requestForProposal.bidderAwardStatus === 'declined' &&
											  parseFloat(nullSafeGetOrElse('incurredCostTotalBeforeTax', proposal, 0)) >
													0 ? (
												<Popover content="Create Work Order for incurred cost">
													<Button loading={workOrderCreating} onClick={onCreateWorkOrder}>
														Create Work Order
													</Button>
												</Popover>
											) : null}
										</div>
										{nullSafeGet('attachments.0', proposal) ? (
											<>
												<div className="sectionHeaderWrapper" style={{ marginBottom: 8 }}>
													<span className="sectionHeaderText">File Attachments</span>
												</div>
												<FileUploader
													roleType="supplier"
													defaultFileList={proposal.attachments || []}
													uploaderType={'readOnly'}
												/>
												<Divider />
											</>
										) : null}
										<div className="sectionHeaderWrapper" style={{ marginBottom: 8 }}>
											<span className="sectionHeaderText">Incurred Cost</span>
											<span className="sectionHeaderText2">
												{currencyFormatter(0)(proposal.incurredCostTotalBeforeTax)}
											</span>
										</div>
										<Divider />
										<div className="sectionHeaderWrapper" style={{ marginBottom: 8 }}>
											<span className="sectionHeaderText">Labor</span>
											<span className="sectionHeaderText2">
												{currencyFormatter(0)(proposal.laborTotalBeforeTax)}
											</span>
										</div>
										<Divider />
										<div className="sectionHeaderWrapper" style={{ marginBottom: 8 }}>
											<span className="sectionHeaderText">Materials</span>
											<span className="sectionHeaderText2">
												{currencyFormatter(0)(proposal.materialTotalBeforeTax)}
											</span>
										</div>
										<Divider />
										<div className="sectionHeaderWrapper">
											<span className="sectionHeaderText">Travel</span>
											<span className="sectionHeaderText2">
												{currencyFormatter(0)(parseFloat(proposal.travelTotalBeforeTax) || 0)}
											</span>
										</div>
										<Divider />
										<div className="sectionHeaderWrapper">
											<span className="sectionHeaderText">Freight</span>
											<span className="sectionHeaderText2">
												{currencyFormatter(0)(parseFloat(proposal.freightTotalBeforeTax) || 0)}
											</span>
										</div>
										<Divider />
										<div className="sectionHeaderWrapper">
											<span className="sectionHeaderText">Miscellaneous</span>
											<span className="sectionHeaderText2">
												{currencyFormatter(0)(parseFloat(proposal.miscTotalBeforeTax) || 0)}
											</span>
										</div>
										<Divider />
										<div className="sectionHeaderWrapper">
											<span className="sectionHeaderText">Total Before Tax</span>
											<span className="sectionHeaderText2">
												{currencyFormatter(0)(proposal.totalBeforeTax)}
											</span>
										</div>
										<Divider />
										<div className="sectionHeaderWrapper">
											<span className="sectionHeaderText">Tax</span>
											<span className="sectionHeaderText2">
												{currencyFormatter(0)(proposal.tax)}
											</span>
										</div>
										<Divider />
										<div className="sectionHeaderWrapper">
											<span className="sectionHeaderText" style={{ fontWeight: 600 }}>
												Total After Tax
											</span>
											<span className="sectionHeaderText2" style={{ fontWeight: 600 }}>
												{currencyFormatter(0)(proposal.totalAfterTax)}
											</span>
										</div>
									</Card>
								</div>
							))}
						</div>
					) : (
						<Row style={{ margin: '0.5em -8px' }} gutter={16}>
							<Col span={24}>
								<Card>
									<EmptyState
										graphic={
											<img
												style={{ marginBottom: 8 }}
												src="https://s3.amazonaws.com/mock-data-assets/categories/images/bank terminal.svg"
												alt="You haven't submitted a proposal."
											/>
										}
										headline={"You haven't submitted a proposal."}
										body={
											canCreateProposal ? (
												<div style={{ textAlign: 'center' }}>
													<div style={{ maxWidth: 440, marginBottom: 16 }}>
														Interested in this opportunity?
													</div>
													<Link
														to={`/supplier/opportunities/detail/${requestForProposalId}/newProposal?workOrderId=${requestForProposal.workOrder.id}`}
														style={{ marginRight: 16 }}
													>
														<Button type="primary">Create a proposal</Button>
													</Link>
												</div>
											) : null
										}
									/>
								</Card>
							</Col>
						</Row>
					)}
				</Col>

				<Col span={8}>
					{customerFileAttachmentsAvailable ? (
						<Row style={{ margin: '0.5em -8px' }}>
							<Col span={24}>
								<Card title="Customer File Attachments">
									<FileUploader
										roleType="supplier"
										defaultFileList={requestForProposal.attachments || []}
										uploaderType={'readOnly'}
									/>
								</Card>
							</Col>
						</Row>
					) : null}
				</Col>
			</Row>
		</Content>
	);
};

const mapStateToProps = (state, ownProps) => ({
	requestsForProposalFetching: state.requests_for_proposal.fetching,
	requestForProposal: getEntityById(ownProps.match.params.id, state.requests_for_proposal),
	proposals: state.proposals,
	proposalsFetching: state.proposals.fetching,
	currentUser: state.session.currentUser,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	updateStatus: (entity, status) => dispatch(updateBidderStatus(ownProps.userType)(entity, status)),
	getRequestForProposal: (id) => dispatch(requestsForProposalRestCrudThunksForSupplier.readOne(id)),
	getProposals: (params, targetCollection) =>
		dispatch(proposalsRestCrudThunksForSupplier.read(params, targetCollection)),
	createWorkOrder: (entity) => dispatch(workOrdersRestCrudThunksForSupplier.create(entity)),
});

const ComponentWithoutUserType = withRouter(
	connect(mapStateToProps, mapDispatchToProps)(SupplierRFPDetailDetailsPage)
);

export default connect(
	(state) => ({
		userType: (state as any).session.userType,
	}),
	() => ({})
)(ComponentWithoutUserType);
